// src/components/CodeBlock/Preview.tsx
import React, { useEffect, useRef, useState } from 'react';
import type { CodeBlockState } from './CodeBlock';
import {
  buildSrcDoc,
  scopeHTML,
  clampFrameHeight,
  FRAME_HEIGHT_MESSAGE
} from '../../utils/sanitize.js';
import { sanitizeHtml, sanitizeEmbeddedDocument } from '../../utils/sanitizeHtml';
import '../../utils/prism.js'; // side-effect import to register languages/plugins
// Prism is attached globally by prism.ts; declare for TS
declare const Prism: any;

type Props = { state: CodeBlockState; showMeta?: boolean };

export function Preview({ state, showMeta = true }: Props) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [frameHeight, setFrameHeight] = useState(400);

  // The preview frame is sandboxed without allow-same-origin, so its document
  // is opaque to us and its height cannot be read directly. It measures itself
  // and posts the number instead; see FRAME_HEIGHT_REPORTER in utils/sanitize.js.
  //
  // Declared before the displayCode early-return below so the hook order stays
  // stable across both render modes.
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      const frame = iframeRef.current;
      // The only trust check that means anything here: the message has to come
      // from the exact frame this component owns. event.origin is "null" for a
      // sandboxed document, so it proves nothing — any other page or frame
      // could claim it. Identity of the source window cannot be spoofed.
      if (!frame || !event.source || event.source !== frame.contentWindow) return;

      const payload = event.data as { type?: unknown; height?: unknown } | null;
      if (!payload || payload.type !== FRAME_HEIGHT_MESSAGE) return;

      // Untrusted number: rejected unless finite, then clamped.
      const height = clampFrameHeight(payload.height);
      if (height === null) return;
      setFrameHeight(height);
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  if (state.mode === 'displayCode') {
    const lang = state.language === 'text' ? 'none' : state.language;
    // Prism handles escaping; we pass the raw string
    const highlighted = Prism.highlight(
      state.content ?? '',
      Prism.languages[lang] || Prism.languages.markup,
      lang
    );
    const showNumbers = state.options.showLineNumbers !== false;
    return (
      <pre className={showNumbers ? 'line-numbers' : undefined}>
        <code
          className={`language-${lang}`}
          // Prism escapes its input, so this only ever emits <span class>.
          // Sanitized anyway so no path into the DOM is an exception.
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(highlighted) }}
        />
      </pre>
    );
  }

  // Render as Rich HTML (sanitized + sandboxed iframe)
  const sanitized: string = sanitizeEmbeddedDocument(state.content ?? '');
  const htmlForScope = state.customCss
    ? sanitized.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '') + `<style>${state.customCss}</style>`
    : sanitized;
  const scoped = scopeHTML(htmlForScope, `codeblock-${state.id}`);
  const srcdoc = buildSrcDoc(scoped.html);

  // Scripts run so preview content behaves like a real page, but WITHOUT
  // allow-same-origin: pairing the two puts the frame back in the parent's
  // origin and voids the sandbox entirely, letting framed content read every
  // lesson in localStorage. The frame reports its height by postMessage
  // precisely so we do not need same-origin access to measure it.
  const sandbox = 'allow-scripts allow-forms';

  return (
    <div>
      {showMeta && scoped.hasStyle && (
        <div style={{ fontSize: '12px', color: '#666', marginBottom: 4 }}>
          Styles are scoped to this block
        </div>
      )}
      <iframe
        ref={iframeRef}
        key={`iframe-${state.id}`}
        className="render-frame"
        title={`Code preview ${state.id}`}
        sandbox={sandbox}
        srcDoc={srcdoc}
        style={{
          width: '100%',
          height: frameHeight,
          border: '1px solid #e5e7eb',
          borderRadius: '8px'
        }}
      />
    </div>
  );
}
